/**
 * INTERNAL AUDIT, read from the database.
 *
 * Ledger row H4's remainder was "the Portal screen is not built" for the
 * retention clock. Building it opened the Internal Audit card, and the card had
 * a larger hole than the one being closed.
 *
 * WHAT WAS MEASURED, before script 217 was written:
 *
 *   admin_flags                            19 rows, every one of them OPEN
 *   functions that WRITE it                 7
 *   functions that READ it for a person     0
 *   RLS policies on it                      1, gating on 'all'
 *   active staff holding 'all'              1 of 6
 *   active staff holding view_admin_flags   2 of 6
 *
 * And then, run as real signed-in seats rather than read off the policy:
 *
 *   the seat that HOLDS view_admin_flags read   0 of 19
 *   the single 'all' holder read               19 of 19
 *
 * So the permission named for the table — "See raised fraud and anomaly flags",
 * registered in script 48 and granted to two seats — granted nothing on it.
 * Nineteen open flags, seven producers, and one person on the whole platform
 * who could see them, through a wildcard rather than through the permission.
 *
 * That is H2's shape in a new place. H2 was four sanctions tables whose policies
 * named a permission present in no row of the catalogue; here the permission
 * exists and is held, and it is the TABLE that never learned about it.
 *
 * TWO CALLS, AND THE SPLIT IS THE SPECIFICATION.
 *
 *   flags   — the queue. A flag is a subject joined to an allegation, so this
 *             is NOT reason-free: p_reason is a required argument of the
 *             database function and the log row is written before the first
 *             flag is resolved. The screen puts a ReasonGate in front of it,
 *             and the gate is not the security — the database is. It returns
 *             the subject's Connect Code and never their name (Prohibition 6).
 *
 *   clock   — whether the POPIA retention job is actually running. No personal
 *             data and no reason: a schedule and two counts. It exists because
 *             fn_retention_runs alone cannot tell "nothing has been due" from
 *             "this database never started the clock", and on any database
 *             without pg_cron the second is true.
 *
 * WHAT THIS MODULE CANNOT DO, and it is not an oversight: it cannot resolve or
 * dismiss a flag. admin_flags carries status, resolved_at, resolved_by and
 * resolution_note, and no function in the database writes any of them. That is
 * the write half and it belongs to Portal P5, where reason capture and the OID
 * chain are specified. A resolve button shipped ahead of that would be the
 * fastest way to get it wrong.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

/**
 * The database's own floor, and the reason it is stated here.
 *
 * fn_admin_flags refuses a reason under eight characters. It has to do that
 * itself, because fn_log_my_admin_action — which several scripts' comments and
 * this Portal's own ReasonGate describe as "refusing a blank reason" — does no
 * such thing. Measured: it checks the account, the MFA state, the permission
 * and the export grant, and never looks at the reason at all. Of 42 functions
 * in the database taking a p_reason, nine validate it.
 *
 * Eight, because that is the floor fn_sever_staff_account, fn_reset_staff_mfa
 * and fn_withdraw_staff_invitation already use. Not a new number.
 */
export const MIN_FLAG_REASON = 8;

export const FLAG_STATUSES = [
  { key: 'open', label: 'Open' },
  { key: 'investigating', label: 'Being looked at' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'all', label: 'Everything' },
];

/**
 * What a flag type means, in the words of the thing that raises it.
 *
 * Unmapped types render their raw key rather than a friendly guess: seven
 * different functions write this table and an eighth could be added tomorrow,
 * and inventing a description for a producer this file has never seen is how a
 * desk ends up reading a sentence nobody wrote.
 */
export const FLAG_TYPES = {
  landlord_non_confirmation:
    'A landlord did not confirm a payment the tenant says was made. Elapsed '
    + 'time never counts against the tenant (Immutable Rule 2), so this is '
    + 'raised for a person to look at rather than settled automatically.',
  invoice_exceeds_declined_variance:
    'An invoice came in above the quote by more than the client had agreed to '
    + 'absorb.',
};

/**
 * The flag queue. There is no version of this without a reason, because there
 * is no version of the database function without one.
 */
export async function fetchAdminFlags(reason, status = 'open') {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_admin_flags', {
    p_reason: reason,
    p_status: status,
    p_limit: 200,
  });
  if (error) return fail(error.message || 'The flags could not be loaded.');
  return { data: data || [], error: null };
}

/**
 * Whether the retention clock is running. Reason-free deliberately: this
 * returns a cron schedule and two counts, and asking somebody to justify
 * checking that a scheduled job ran would teach them to type anything.
 */
export async function fetchRetentionClock() {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_retention_clock_status');
  if (error) return fail(error.message || 'The retention clock could not be read.');
  return { data: Array.isArray(data) ? data[0] || null : data, error: null };
}

/** The runs themselves, for the desk that wants the detail behind the summary. */
export async function fetchRetentionRuns(limit = 20) {
  if (!isSupabaseConfigured()) return fail('This portal cannot reach the database.');
  const { data, error } = await supabase.rpc('fn_retention_runs', { p_limit: limit });
  if (error) return fail(error.message || 'The retention runs could not be loaded.');
  return { data: data || [], error: null };
}

/**
 * How the clock reads, as a sentence rather than four booleans.
 *
 * The order matters and it is the point of the whole function: "not scheduled"
 * is reported BEFORE "no runs yet", because a database where the job was never
 * created would otherwise show an empty list and look like a quiet month.
 */
export function clockVerdict(clock) {
  if (!clock) return { tone: 'quiet', headline: 'Not read yet.', detail: null };

  if (!clock.cron_available) {
    return {
      tone: 'critical',
      headline: 'The retention clock is not running.',
      detail:
        'pg_cron is not enabled on this database, so the nightly job does not '
        + 'exist to be run. On Supabase: CREATE EXTENSION IF NOT EXISTS pg_cron, '
        + 'then re-apply script 202.',
    };
  }
  if (!clock.is_scheduled) {
    return {
      tone: 'critical',
      headline: 'The retention clock is not scheduled.',
      detail:
        'pg_cron is available but no job named orchid_retention_expiry exists. '
        + 'Re-applying script 202 creates it.',
    };
  }
  if (clock.is_overdue) {
    return {
      tone: 'warning',
      headline: clock.total_runs === 0
        ? 'Scheduled, and it has never run.'
        : 'Scheduled, and it has not run when it should have.',
      detail: clock.total_runs === 0
        ? 'The job exists but has not fired once.'
        : `Last run ${Math.round(clock.hours_since_run)} hours ago, against a daily schedule.`,
    };
  }
  if (clock.last_run_ok === false) {
    return {
      tone: 'critical',
      headline: 'The last retention run failed.',
      detail: clock.last_error || 'It started and did not finish.',
    };
  }
  return {
    tone: 'green',
    headline: 'The retention clock is running.',
    detail: `${clock.total_runs} run${clock.total_runs === 1 ? '' : 's'} recorded, `
      + `the last ${Math.round(clock.hours_since_run)} hours ago.`,
  };
}
